/** Aligned with backend Docling defaults (240 × 5s ≈ 20 min). */
export const PROCESSING_POLL_INTERVAL_MS = 5000;
export const PROCESSING_MAX_POLL_ATTEMPTS = 240;
export const PROCESSING_MAX_CONSECUTIVE_ERRORS = 5;

export class ProcessingPollTimeoutError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "ProcessingPollTimeoutError";
  }
}

export function isTerminalProcessingStatus(status: string): boolean {
  return status === "COMPLETED" || status === "FAILED";
}

export async function sleep(ms: number): Promise<void> {
  await new Promise((resolve) => setTimeout(resolve, ms));
}

export interface PollUntilTerminalOptions<T> {
  intervalMs?: number;
  maxAttempts?: number;
  maxConsecutiveErrors?: number;
  onStatus?: (status: T | null | undefined) => void;
}

export async function pollUntilTerminal<T extends string>(
  checkStatus: () => Promise<T | null | undefined>,
  options: PollUntilTerminalOptions<T> = {},
): Promise<T> {
  const intervalMs = options.intervalMs ?? PROCESSING_POLL_INTERVAL_MS;
  const maxAttempts = options.maxAttempts ?? PROCESSING_MAX_POLL_ATTEMPTS;
  const maxConsecutiveErrors =
    options.maxConsecutiveErrors ?? PROCESSING_MAX_CONSECUTIVE_ERRORS;

  let consecutiveErrors = 0;

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    try {
      const status = await checkStatus();
      consecutiveErrors = 0;
      options.onStatus?.(status);

      if (status && isTerminalProcessingStatus(status)) {
        return status;
      }
    } catch {
      consecutiveErrors++;
      if (consecutiveErrors >= maxConsecutiveErrors) {
        throw new Error("Unable to check processing status");
      }
    }

    if (attempt < maxAttempts - 1) {
      await sleep(intervalMs);
    }
  }

  throw new ProcessingPollTimeoutError("Processing timed out");
}
